import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Loader2, CheckCircle2, XCircle, ArrowLeft, ExternalLink,
  ShieldAlert, PlayCircle, StopCircle, Clock, Zap, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorText } from "@/lib/apiError";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Country dropdown mirrors what /admin/verify uses; kept short here
// because these are the countries with the most editorial-tier gaps.
const COUNTRIES = [
  ["", "— pick a country —"],
  ["UK", "UK"],
  ["US", "US"],
  ["Germany", "Germany"],
  ["France", "France"],
  ["Ireland", "Ireland"],
  ["Netherlands", "Netherlands"],
  ["Singapore", "Singapore"],
  ["United Arab Emirates", "UAE"],
  ["Canada", "Canada"],
  ["Switzerland", "Switzerland"],
  ["Italy", "Italy"],
  ["Spain", "Spain"],
  ["India", "India"],
  ["South Korea", "South Korea"],
  ["Australia", "Australia"],
];

/**
 * /admin/ai-fetch-batch — kick off a country-wide AI-Fetch job and
 * watch it run. Never writes to the DB directly — the editor still
 * opens each degree in /admin/verify/:id and clicks Apply→Save.
 */
export default function AdminAIFetchBatch() {
  const { user, login } = useAuth();
  const [state, setState] = useState({ loading: true, forbidden: false });
  const [filters, setFilters] = useState({ country: "", tier: "editorial", limit: 30, skip_recent_days: 7, require_evidence_url: true });
  const [job, setJob] = useState(null);
  const [confirmApply, setConfirmApply] = useState({ open: false, min_confidence: "high" });
  const [applying, setApplying] = useState(false);
  const [applyResult, setApplyResult] = useState(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        await api.get("/admin/whoami");
        setState({ loading: false, forbidden: false });
      } catch {
        setState({ loading: false, forbidden: true });
      }
    })();
    return () => { if (pollTimer.current) clearTimeout(pollTimer.current); };
  }, []);

  const poll = useCallback(async (jobId) => {
    try {
      const { data } = await api.get(`/admin/ai-fetch/batch/${jobId}`);
      setJob(data);
      if (data.status === "queued" || data.status === "running") {
        pollTimer.current = setTimeout(() => poll(jobId), 3000);
      }
    } catch (err) {
      toast.error(apiErrorText(err, "Poll failed"));
    }
  }, []);

  const start = async () => {
    if (!filters.country) {
      toast.error("Pick a country first.");
      return;
    }
    try {
      const { data } = await api.post("/admin/ai-fetch/batch/start", filters);
      toast.success(`Batch job started: ${data.job_id.slice(0, 8)}…`);
      setJob({ job_id: data.job_id, status: "queued", processed: 0, total: 0, results: [] });
      poll(data.job_id);
    } catch (err) {
      toast.error(apiErrorText(err, "Start failed"));
    }
  };

  const cancel = async () => {
    if (!job?.job_id) return;
    try {
      await api.post(`/admin/ai-fetch/batch/${job.job_id}/cancel`);
      toast.success("Cancelled — no more degrees will be processed");
      poll(job.job_id);
    } catch (err) {
      toast.error(apiErrorText(err, "Cancel failed"));
    }
  };

  const bulkApply = async () => {
    if (!job?.job_id) return;
    setApplying(true);
    setApplyResult(null);
    try {
      const { data } = await api.post(
        `/admin/ai-fetch/batch/${job.job_id}/bulk-apply`,
        { min_confidence: confirmApply.min_confidence, include_entry_requirements: true },
      );
      setApplyResult(data);
      setConfirmApply({ ...confirmApply, open: false });
      toast.success(
        `Applied ${data.fields_applied} field(s) across ${data.degrees_touched} degree(s). ${data.skipped.length} skipped.`
      );
      // Reload the job so it shows the audit stamp
      poll(job.job_id);
    } catch (err) {
      toast.error(apiErrorText(err, "Bulk apply failed"));
    } finally {
      setApplying(false);
    }
  };

  if (state.loading) {
    return (
      <div className="max-w-4xl mx-auto px-6 py-16 text-stone-500 font-mono text-sm" data-testid="admin-batch-loading">
        <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
      </div>
    );
  }
  if (state.forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16" data-testid="admin-batch-forbidden">
        <div className="p-8 border border-stone-300 rounded-lg bg-white">
          <ShieldAlert className="h-8 w-8 text-stone-500" />
          <h1 className="mt-3 font-editorial text-2xl">Admin access required</h1>
          {!user && <Button className="mt-4" onClick={login}>Sign in with Google</Button>}
        </div>
      </div>
    );
  }

  const pct = job?.total ? Math.round((job.processed / job.total) * 100) : 0;
  const okCount = (job?.results || []).filter((r) => r.ok).length;
  const failCount = (job?.results || []).filter((r) => !r.ok).length;

  return (
    <div className="max-w-5xl mx-auto px-6 py-10" data-testid="admin-batch-page">
      <Link to="/admin" className="inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-stone-500 hover:text-stone-900">
        <ArrowLeft className="h-3 w-3" /> Newsroom
      </Link>

      <div className="mt-3">
        <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">Editorial CMS · Bulk verify</div>
        <h1 className="mt-1 font-editorial text-3xl flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-indigo-700" /> Batch AI-Fetch
        </h1>
        <p className="mt-2 text-stone-600 text-sm max-w-2xl">
          Run assisted extraction across an entire country's editorial-tier degrees.
          Each row's stored evidence URL is re-fetched; Claude proposes tuition,
          offer rate, IELTS/TOEFL and entry requirements with quotes. Nothing
          writes to the DB — you still open each degree in{" "}
          <Link to="/admin/verify" className="underline">/admin/verify</Link> and
          click Apply→Save. Rate limited to 1 LLM call at a time.
        </p>
      </div>

      <section className="mt-6 p-5 border border-stone-300 rounded-xl bg-white grid md:grid-cols-4 gap-4 items-end">
        <div>
          <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">Country</label>
          <select
            value={filters.country}
            onChange={(e) => setFilters({ ...filters, country: e.target.value })}
            className="mt-1 w-full h-9 px-3 border border-stone-300 rounded-md text-sm bg-white"
            data-testid="admin-batch-country-select"
          >
            {COUNTRIES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">Tier</label>
          <select
            value={filters.tier}
            onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
            className="mt-1 w-full h-9 px-3 border border-stone-300 rounded-md text-sm bg-white"
            data-testid="admin-batch-tier-select"
          >
            <option value="editorial">Editorial only</option>
            <option value="">All tiers</option>
          </select>
        </div>
        <div>
          <label className="text-[11px] font-underground-sc tracking-wider text-stone-600">Limit</label>
          <Input
            type="number"
            min="1"
            max="200"
            value={filters.limit}
            onChange={(e) => setFilters({ ...filters, limit: Number(e.target.value || 30) })}
            className="mt-1"
            data-testid="admin-batch-limit-input"
          />
        </div>
        <div>
          <label
            className="text-[11px] font-underground-sc tracking-wider text-stone-600"
            title="Skip degrees the batch already touched in the last N days. Set 0 to force re-fetch of the same set."
          >
            Skip recent (days)
          </label>
          <Input
            type="number"
            min="0"
            max="365"
            value={filters.skip_recent_days}
            onChange={(e) => setFilters({ ...filters, skip_recent_days: Number(e.target.value || 0) })}
            className="mt-1"
            data-testid="admin-batch-skip-recent-input"
          />
        </div>
        <div>
          <Button
            className="w-full"
            onClick={start}
            disabled={job && (job.status === "queued" || job.status === "running")}
            data-testid="admin-batch-start-btn"
          >
            <PlayCircle className="h-4 w-4 mr-1" /> Start batch
          </Button>
        </div>
      </section>

      <div className="mt-3 flex items-center gap-2 text-xs">
        <input
          id="require-evidence-url"
          type="checkbox"
          checked={filters.require_evidence_url}
          onChange={(e) => setFilters({ ...filters, require_evidence_url: e.target.checked })}
          data-testid="admin-batch-require-evidence-checkbox"
        />
        <label htmlFor="require-evidence-url" className="text-stone-600 select-none">
          Require per-degree evidence URL <span className="text-stone-400">(recommended — skips rows that would only crawl the university homepage)</span>
        </label>
      </div>

      {job && (
        <section className="mt-6 p-5 border border-stone-300 rounded-xl bg-white" data-testid="admin-batch-progress">
          <div className="flex items-baseline justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] font-underground-sc tracking-widest text-stone-500">
                Job {job.job_id?.slice(0, 8)}…
              </div>
              <div className="font-editorial text-xl flex items-center gap-2">
                {job.status === "running" && <Loader2 className="h-4 w-4 animate-spin text-indigo-700" />}
                {job.status === "done" && <CheckCircle2 className="h-4 w-4 text-emerald-700" />}
                {job.status === "cancelled" && <StopCircle className="h-4 w-4 text-stone-500" />}
                {job.status === "queued" && <Clock className="h-4 w-4 text-stone-500" />}
                <span className="capitalize">{job.status}</span>
                <span className="text-stone-400 text-sm">· {job.processed}/{job.total || "?"} · {pct}%</span>
              </div>
            </div>
            {(job.status === "running" || job.status === "queued") && (
              <Button size="sm" variant="outline" onClick={cancel} data-testid="admin-batch-cancel-btn">
                <StopCircle className="h-3.5 w-3.5 mr-1" /> Cancel
              </Button>
            )}
            {job.status === "done" && (job.results || []).some((r) => r.ok) && !job.bulk_applied_at && (
              <Button
                size="sm"
                onClick={() => setConfirmApply({ open: true, min_confidence: "high" })}
                data-testid="admin-batch-bulk-apply-btn"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
              >
                <Zap className="h-3.5 w-3.5 mr-1" /> Bulk apply high-confidence
              </Button>
            )}
            {job.bulk_applied_at && (
              <span
                data-testid="admin-batch-bulk-applied-stamp"
                className="inline-flex items-center gap-1 text-[10px] font-underground-sc tracking-wider bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2 py-0.5"
              >
                <CheckCircle2 className="h-3 w-3" />
                Bulk-applied ·{" "}
                {job.bulk_applied_summary?.fields_applied || 0} fields ·{" "}
                {job.bulk_applied_summary?.degrees_touched || 0} degrees ·{" "}
                {job.bulk_applied_summary?.min_confidence}+
              </span>
            )}
          </div>
          <div className="mt-3 h-2 bg-stone-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-indigo-600 transition-all"
              style={{ width: `${pct}%` }}
              data-testid="admin-batch-progressbar"
            />
          </div>
          <div className="mt-3 flex items-center gap-4 text-xs font-mono text-stone-600">
            <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3 text-emerald-700" /> {okCount} extracted</span>
            <span className="flex items-center gap-1"><XCircle className="h-3 w-3 text-stone-500" /> {failCount} skipped</span>
          </div>

          <ul className="mt-5 divide-y divide-stone-100 border border-stone-200 rounded-lg bg-white max-h-[500px] overflow-y-auto" data-testid="admin-batch-results">
            {(job.results || []).map((r, i) => (
              <li key={r.degree_id || i} className="p-3 text-xs flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    {r.ok ? (
                      <CheckCircle2 className="h-3.5 w-3.5 text-emerald-700 shrink-0" />
                    ) : (
                      <XCircle className="h-3.5 w-3.5 text-stone-400 shrink-0" />
                    )}
                    <Link to={`/admin/verify/${encodeURIComponent(r.degree_id)}`} className="font-mono truncate hover:underline">
                      {r.official_title || r.degree_id}
                    </Link>
                  </div>
                  <div className="mt-0.5 text-stone-500 font-mono text-[10px]">
                    {r.ok
                      ? `${r.non_null_fields ?? 0} candidate fields extracted`
                      : (r.reason || "failed")}
                  </div>
                </div>
                {r.ok && (
                  <Link
                    to={`/admin/verify/${encodeURIComponent(r.degree_id)}`}
                    className="text-[10px] font-underground-sc tracking-wider text-indigo-700 hover:text-indigo-900 flex items-center gap-1 shrink-0"
                  >
                    Review <ExternalLink className="h-3 w-3" />
                  </Link>
                )}
              </li>
            ))}
            {(job.results || []).length === 0 && (
              <li className="p-6 text-center text-stone-500 text-xs">Waiting for the first result…</li>
            )}
          </ul>
        </section>
      )}

      {/* Bulk-apply confirmation modal — writes to db.degrees, so we
          make the editor tick through a summary before firing. */}
      <Dialog open={confirmApply.open} onOpenChange={(v) => setConfirmApply({ ...confirmApply, open: v })}>
        <DialogContent className="max-w-md" data-testid="admin-batch-bulk-confirm">
          <DialogHeader>
            <DialogTitle className="font-editorial text-2xl flex items-center gap-2">
              <Zap className="h-5 w-5 text-emerald-700" /> Bulk apply high-confidence
            </DialogTitle>
            <DialogDescription className="text-xs">
              This writes directly to the degree records — no per-record review.
              Only fields whose AI extraction confidence is <strong>{confirmApply.min_confidence}</strong> or above get applied.
              Everything else stays untouched for you to review manually at /admin/verify.
            </DialogDescription>
          </DialogHeader>

          <div className="mt-2 space-y-3 text-sm">
            <div>
              <div className="text-[10px] font-underground-sc tracking-wider text-stone-500">Minimum confidence</div>
              <div className="mt-1 flex gap-2">
                {["high", "medium"].map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`admin-batch-confidence-${c}`}
                    onClick={() => setConfirmApply({ ...confirmApply, min_confidence: c })}
                    className={
                      "text-xs font-underground-sc tracking-wider border rounded-full px-3 py-1 " +
                      (confirmApply.min_confidence === c
                        ? "bg-emerald-700 text-white border-emerald-700"
                        : "border-stone-300 text-stone-600 hover:border-stone-500")
                    }
                  >
                    {c === "high" ? "High only (recommended)" : "Medium+"}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-md text-xs text-amber-900 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>
                Every touched field will be stamped with{" "}
                <span className="font-mono">source: "AI-Fetch (bulk)"</span> and{" "}
                <span className="font-mono">confidence: "verified"</span>. The AI's own confidence is preserved as{" "}
                <span className="font-mono">ai_confidence</span> so you can audit later.
              </span>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" size="sm" onClick={() => setConfirmApply({ ...confirmApply, open: false })} disabled={applying}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={bulkApply}
              disabled={applying}
              data-testid="admin-batch-bulk-apply-confirm-btn"
              className="bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {applying ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Applying…</> : <><Zap className="h-3.5 w-3.5 mr-1.5" /> Apply now</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {applyResult && (
        <section
          className="mt-6 p-5 border border-emerald-300 rounded-xl bg-emerald-50/50"
          data-testid="admin-batch-bulk-apply-result"
        >
          <div className="flex items-center gap-2 text-emerald-800">
            <CheckCircle2 className="h-5 w-5" />
            <span className="font-editorial text-lg">Bulk apply complete</span>
          </div>
          <div className="mt-2 text-sm text-emerald-900">
            Applied <strong>{applyResult.fields_applied}</strong> field(s) across{" "}
            <strong>{applyResult.degrees_touched}</strong> degree(s) at min. confidence{" "}
            <strong>{applyResult.min_confidence}</strong>.
            <br />
            <span className="text-emerald-800/80">
              {applyResult.skipped?.length || 0} row(s) skipped (below threshold or no appliable fields).
            </span>
          </div>
          {applyResult.per_degree?.length > 0 && (
            <ul className="mt-3 text-xs font-mono space-y-1 max-h-[200px] overflow-y-auto">
              {applyResult.per_degree.map((d) => (
                <li key={d.degree_id}>
                  <CheckCircle2 className="h-3 w-3 text-emerald-700 inline mr-1.5" />
                  {d.degree_id} · <span className="text-stone-500">{d.fields_applied} fields · {d.confidences.join(", ")}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
